import {DataTestAttrs} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import cn from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHT, GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import SearchableUnitDropdown, {UnitOptionName} from './SearchableUnitDropdown';
import {HydratedUnit, hydrateGqlUnit} from './util';

export function getOptionsForFormulaCanvas(allUnitTypes: UnitTypesQuery['unitTypes']): Array<OptionData> {
  const options: Array<OptionData> = [];
  const nonArchivedUnitTypes = allUnitTypes.filter((unitType) => !unitType.archiveData.archived);

  options.push({name: 'Base Units', isHeader: true});
  for (const unitType of nonArchivedUnitTypes) {
    options.push({
      name: (
        <UnitOptionName
          unitName={unitType.baseUnit.name}
          unitSymbol={unitType.baseUnit.symbol}
          unitTypeName={unitType.name}
        />
      ),
      value: hydrateGqlUnit(unitType.baseUnit, unitType),
      pureTextName: `${unitType.name} ${unitType.baseUnit.name} ${unitType.baseUnit.symbol}`,
    });
  }

  for (const unitType of nonArchivedUnitTypes) {
    const eligibleUnits = unitType.units.filter(
      (u) => !u.archiveData.archived && u.id !== unitType.baseUnit.id
    );
    if (eligibleUnits.length > 0) {
      options.push({name: unitType.name, isHeader: true});
      for (const unit of eligibleUnits) {
        options.push({
          name: <UnitOptionName unitName={unit.name} unitSymbol={unit.symbol} />,
          value: hydrateGqlUnit(unit, unitType),
          pureTextName: `${unit.name} ${unit.symbol}`,
        });
      }
    }
  }

  return options;
}

interface ActionButtonsProps {
  dataTestAttrs: DataTestAttrs;
  hidden: boolean;
  showAddTerm: boolean;
  showDelete: boolean;
  showDivide?: boolean;
  onAddTerm: () => void;
  onDelete: () => void;
  onDivide?: () => void;
}
function ActionButtons(props: ActionButtonsProps): JSX.Element | null {
  const {dataTestAttrs, showAddTerm, showDelete, showDivide, onAddTerm, onDelete, onDivide} = props;
  if (!showAddTerm && !showDelete && !showDivide) {
    return null;
  }
  return (
    <div
      className={css(styles.actionButtons)}
      style={{visibility: props.hidden ? 'hidden' : 'visible'}}
      {...getTestProps(dataTestAttrs)}
    >
      {showAddTerm && (
        <span className={css(styles.actionButton)}>
          <BenchlingIcon
            onClick={onAddTerm}
            icon="xmark"
            dataTestAttrs={{component: 'ActionButtons', element: 'addTermButton'}}
          />
        </span>
      )}
      {showDivide && (
        <span className={css(styles.actionButton)}>
          <BenchlingIcon
            onClick={onDivide}
            icon="divide"
            dataTestAttrs={{component: 'ActionButtons', element: 'divideTermButton'}}
          />
        </span>
      )}
      {showDelete && (
        <span className={css(styles.actionButton)}>
          <BenchlingIcon
            onClick={onDelete}
            icon="trash-can"
            dataTestAttrs={{component: 'ActionButtons', element: 'deleteTermButton'}}
          />
        </span>
      )}
    </div>
  );
}
interface Props {
  numeratorUnits: Array<HydratedUnit>;
  denominatorUnits: Array<HydratedUnit>;
  setNumeratorUnits: (fn: (units: Array<HydratedUnit>) => Array<HydratedUnit>) => void;
  setDenominatorUnits: (fn: (units: Array<HydratedUnit>) => Array<HydratedUnit>) => void;
}

export default function FormulaCanvas(props: Props): JSX.Element {
  const {numeratorUnits, denominatorUnits, setNumeratorUnits, setDenominatorUnits} = props;
  const [editingNumerator, setEditingNumerator] = useState(true);
  const [editingDenominator, setEditingDenominator] = useState(false);
  const [hoveringNumerator, setHoveringNumerator] = useState(false);
  const [hoveringDenominator, setHoveringDenominator] = useState(false);

  const showDenom = editingDenominator || denominatorUnits.length > 0;
  return (
    <div className={css(styles.canvas)}>
      <div className={css(styles.fraction)}>
        <div
          className="u-vertical-center-items formulaCanvas-numerator"
          onMouseEnter={() => {
            setHoveringNumerator(true);
          }}
          onMouseLeave={() => {
            setHoveringNumerator(false);
          }}
          {...getTestProps({component: 'FormulaCanvas', element: 'numerator'})}
        >
          {numeratorUnits.map((unit, i) => (
            <div key={i} className="u-vertical-center-items">
              <SearchableUnitDropdown
                getOptionsFn={getOptionsForFormulaCanvas}
                className={css(styles.unitDropdown)}
                displaySelectionAsSymbol={true}
                selectedId={unit.id}
                onSelect={(newUnit) => {
                  setNumeratorUnits((units) => [...units.slice(0, i), newUnit!, ...units.slice(i + 1)]);
                }}
              />
              {(i < numeratorUnits.length - 1 || editingNumerator) && <span key={`${i}a`}>x</span>}
            </div>
          ))}
          {editingNumerator && (
            <SearchableUnitDropdown
              getOptionsFn={getOptionsForFormulaCanvas}
              className={css(styles.unitDropdown)}
              displaySelectionAsSymbol={true}
              selectedId={null}
              onSelect={(newUnit) => {
                setEditingNumerator(false);
                setNumeratorUnits((units) => [...units, newUnit!]);
              }}
            />
          )}
          <ActionButtons
            dataTestAttrs={{component: 'FormulaCanvasNumeratorActionButtons', element: 'root'}}
            hidden={!hoveringNumerator}
            showAddTerm={!editingNumerator}
            showDelete={numeratorUnits.length > 0}
            showDivide={!showDenom}
            onAddTerm={() => {
              setEditingNumerator(true);
            }}
            onDelete={() => {
              if (editingNumerator) {
                setEditingNumerator(false);
                return;
              }

              setNumeratorUnits((units) => units.slice(0, -1));
              if (numeratorUnits.length === 1) {
                setEditingNumerator(true);
              }
            }}
            onDivide={() => {
              setEditingDenominator(true);
            }}
          />
        </div>
        <div
          className={cn('u-vertical-center-items', 'formulaCanvas-denominator', {
            [css(styles.fractionLine)]: showDenom,
          })}
          onMouseEnter={() => {
            setHoveringDenominator(true);
          }}
          onMouseLeave={() => {
            setHoveringDenominator(false);
          }}
          {...getTestProps({component: 'FormulaCanvas', element: 'denominator'})}
        >
          {denominatorUnits.map((unit, i) => (
            <div key={i} className="u-vertical-center-items">
              <SearchableUnitDropdown
                className={css(styles.unitDropdown)}
                getOptionsFn={getOptionsForFormulaCanvas}
                displaySelectionAsSymbol={true}
                selectedId={unit.id}
                onSelect={(newUnit) => {
                  setDenominatorUnits((units) => [...units.slice(0, i), newUnit!, ...units.slice(i + 1)]);
                }}
              />
              {(i < denominatorUnits.length - 1 || editingDenominator) && <span>x</span>}
            </div>
          ))}
          {editingDenominator && (
            <SearchableUnitDropdown
              className={css(styles.unitDropdown)}
              getOptionsFn={getOptionsForFormulaCanvas}
              displaySelectionAsSymbol={true}
              selectedId={null}
              onSelect={(newUnit) => {
                setEditingDenominator(false);
                setDenominatorUnits((units) => [...units, newUnit!]);
              }}
            />
          )}
          {showDenom && (
            <ActionButtons
              dataTestAttrs={{component: 'FormulaCanvasDenominatorActionButtons', element: 'root'}}
              hidden={!hoveringDenominator}
              showAddTerm={!editingDenominator}
              showDelete={true}
              onAddTerm={() => {
                setEditingDenominator(true);
              }}
              onDelete={() => {
                if (editingDenominator) {
                  setEditingDenominator(false);
                  return;
                }
                setDenominatorUnits((units) => units.slice(0, -1));
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  canvas: {
    backgroundColor: GRAY_LIGHT,
    minHeight: '120px',
    padding: '8px',
    overflow: 'scroll',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    marginLeft: '8px',
    border: `1px solid ${BLUE_LIGHT}`,
    height: '28px',
    borderRadius: '4px',
    paddingTop: '2px',
  },
  actionButton: {
    margin: '6px',
    cursor: 'pointer',
  },
  fraction: {
    display: 'inline-block',
  },
  fractionLine: {
    borderTop: 1,
    borderTopColor: NEAR_BLACK,
    marginTop: 3.5,
    marginBottom: 3.5,
    borderTopStyle: 'solid',
  },
  unitDropdown: {
    margin: '8px',
    width: '100px',
  },
});
